import { downloadClip, getDownloadClipProgress } from '@/app/services/liveStreaming/liveStreaming';
import { useEffect, useState } from 'react';

import * as Actions from '@/app/store/actions';
import { CircularProgress } from '@material-ui/core';
import DownloadProgressModal from '@/app/customComponents/modals/DownloadProgressModal';
import GradientButton from '@/app/customComponents/GradientButton/GradientButton';
import H264VideoPlayer from './H264VideoPlayer/H264VideoPlayer';
import MjpegVideoPlayer from '@/app/main/subadmin/livestreaming/history/MJPEG/MjpegVideoPlayer';
import { useDispatch } from 'react-redux';
import { useHistoryVideo } from '@/app/main/subadmin/livestreaming/history/HistoryProvider';

const PBJHistoryPlayer = ({ streamData, startTime, date, room, endTime, selectedDate }) => {
	const { clipLoading } = useHistoryVideo();
	const [isLoading, setIsLoading] = useState(false);
	const [downloadJobId, setDownloadJobId] = useState(null);
	const [url, setUrl] = useState('');
	const [downloadLink, setDownloadLink] = useState(null);
	const [downloadModalOpen, setDownloadModalOpen] = useState(false);
	const [downloadProgress, setDownloadProgress] = useState(0);
	const dispatch = useDispatch();

	const isIphone = /iPhone|iPod/.test(navigator.userAgent);

	useEffect(() => {
		if (streamData?.session_url) {
			setUrl(streamData.session_url);
		} else {
			setUrl('');
		}
	}, [streamData]);

	const pollDownloadProgress = async (jobId) => {
		try {
			setDownloadModalOpen(true);
			const progressResponse = await getDownloadClipProgress(jobId);
			const { status, progress, download_url } = progressResponse.data;

			setDownloadProgress(progress);

			// create a temp link, click it, and clean up
			if (status === 'completed' && progress === 100) {
				const link = document.createElement('a');
				link.href = download_url;
				link.setAttribute('download', '');
				link.style.display = 'none';
				document.body.appendChild(link);

				link.click();

				document.body.removeChild(link);

				setDownloadProgress(null);
				setDownloadJobId(null);
				setDownloadLink(download_url);
			} else if (status === 'failed') {
				dispatch(
					Actions.showMessage({
						message: 'Download failed.',
						autoHideDuration: 1500,
						variant: 'error',
					})
				);
				setDownloadProgress(null);
				setDownloadJobId(null);
			} else {
				// Continue polling
				setTimeout(() => pollDownloadProgress(jobId), 3000);
			}
		} catch (error) {
			console.error('Error checking download progress:', error);
			setDownloadProgress(null);
			setDownloadJobId(null);
			setDownloadModalOpen(false);
		}
	};

	const handleDownloadClip = async () => {
		if (!room || !startTime || !endTime || !selectedDate) {
			dispatch(
				Actions.showMessage({
					message: 'Invalid parameters for downloading clip.',
					autoHideDuration: 1500,
					variant: 'error',
				})
			);
			return;
		}

		try {
			setDownloadProgress(0);
			const response = await downloadClip(room.id, `${selectedDate} ${startTime}`, `${selectedDate} ${endTime}`);
			const job_id = response.data.job_id;
			setDownloadJobId(job_id);

			// Start polling for progress
			pollDownloadProgress(job_id);
		} catch (error) {
			console.error('Error downloading clip:', error);
			dispatch(
				Actions.showMessage({
					message: 'Error initiating download.',
					autoHideDuration: 1500,
					variant: 'error',
				})
			);
			setDownloadProgress(null);
		}
	};

	return (
		<div>
			<DownloadProgressModal
				downloadLink={downloadLink}
				open={downloadModalOpen}
				progress={downloadProgress}
				setDownloadLink={setDownloadLink}
				setOpen={setDownloadModalOpen}
			/>
			{isIphone ? (
				<div className="flex flex-col justify-center items-center mt-64">
					<p>History playback is not supported on iOS devices at this time</p>
					<p>Please use another device to view this video</p>
				</div>
			) : (
				<div className="flex justify-start items-start bg-black rounded-lg relative overflow-hidden">
					<div className="w-full h-full relative aspect-ratio-16-9">
						{url ? (
							streamData?.video_type === 'mjpeg' ? (
								<MjpegVideoPlayer
									audioUrl={streamData?.audio_url}
									roomData={room}
									setIsLoading={setIsLoading}
									url={url}
								/>
							) : (
								<H264VideoPlayer
									roomData={room}
									selectedDate={date}
									setIsLoading={setIsLoading}
									startTime={startTime}
									url={url}
								/>
							)
						) : null}
					</div>
					{!startTime ? (
						<div className="stream-not-running-text-history">Please select a clip</div>
					) : clipLoading ? (
						<div className="stream-not-running-text-history bg-black w-full h-full flex justify-center items-center">
							<CircularProgress size={35} />
						</div>
					) : startTime && !streamData?.session_url && !isLoading ? (
						<div className="stream-not-running-text-history">No video</div>
					) : null}
				</div>
			)}
			{streamData?.session_url ? (
				<div className="flex justify-end m-16">
					<GradientButton disabled={downloadJobId !== null} onClick={handleDownloadClip} variant="royal-blue">
						{downloadJobId ? 'Downloading...' : 'Download Clip'}
					</GradientButton>
				</div>
			) : null}
		</div>
	);
};

export default PBJHistoryPlayer;
